import { useCallback, useMemo, useState } from 'react';
import { config } from '../../../../config/config';
import { useScript } from '../../../../utils/hooks/useScript';
import { usePasswordlessAuth } from '../../../common/hooks/usePasswordlessAuth';
import { useAuthTokenSuccess } from '../../../common/hooks/useAuthTokenSuccess';
import { trackEvent } from '../../../analytics/sml/analytics';
import { randomState } from './nonce';
import { useAppleNonce } from './useAppleNonce';

const GIS_URL = 'https://accounts.google.com/gsi/client';
const APPLE_URL = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const joinAppleName = (name?: { firstName?: string; lastName?: string }): string | undefined => {
  const full = [name?.firstName, name?.lastName].filter(Boolean).join(' ').trim();
  return full.length > 0 ? full : undefined;
};

/** Apple JS rejects with `{ error }`: these two codes are the person who closed the sheet. */
const isAppleCancel = (failure: unknown): boolean => {
  const code = (failure as { error?: unknown } | null)?.error;
  return code === 'popup_closed_by_user' || code === 'user_cancelled_authorize';
};

export const useSocialSignIn = () => {
  const googleConfigured = config.googleClientId !== '';
  const appleConfigured = config.appleServicesId !== '';

  useScript(googleConfigured ? GIS_URL : undefined);
  useScript(appleConfigured ? APPLE_URL : undefined);

  const onAuthToken = useAuthTokenSuccess();
  const [error, setError] = useState<Error | null>(null);
  const auth = usePasswordlessAuth({
    // sign_up_finished / log_in_finished segmentation is handled inside
    // usePasswordlessAuth (SML-only), so consumers only forward the token.
    onSuccess: (token: string) => {
      onAuthToken(token);
    },
    onError: setError,
  });
  const appleNonce = useAppleNonce(appleConfigured);

  const signInWithGoogle = useCallback(() => {
    const oauth2 = window.google?.accounts?.oauth2;
    if (!oauth2 || config.googleClientId === '') {
      setError(new Error('Google sign-in unavailable'));
      return;
    }
    // A new state for every attempt. The callback must return the same value.
    const state = randomState();
    const codeClient = oauth2.initCodeClient({
      client_id: config.googleClientId,
      scope: 'openid email profile',
      ux_mode: 'popup',
      state,
      callback: (response) => {
        if (response.state !== state) {
          setError(new Error('Google sign-in failed'));
          return;
        }
        if (response.code) {
          auth.socialSignIn({ provider: 'google', authCode: response.code }).catch(console.error);
        } else {
          trackEvent('signin_cancelled', { method: 'google' });
          setError(new Error('Google sign-in was cancelled'));
        }
      },
      error_callback: (failure) => {
        if (failure.type === 'popup_closed') {
          trackEvent('signin_cancelled', { method: 'google' });
        }
      },
    });
    setError(null);
    codeClient.requestCode();
  }, [auth]);

  // Do not await anything before signIn(). The browser blocks a popup that does not open
  // inside the click handler. So the nonce is fetched and hashed before the click.
  const signInWithApple = useCallback(() => {
    const appleId = window.AppleID;
    if (!appleId || config.appleServicesId === '') {
      setError(new Error('Apple sign-in unavailable'));
      return;
    }
    const prepared = appleNonce.take();
    if (!prepared) {
      setError(new Error('Apple sign-in unavailable'));
      return;
    }
    const attempt = async (): Promise<void> => {
      try {
        appleId.auth.init({
          clientId: config.appleServicesId,
          scope: 'name email',
          redirectURI: config.appleRedirectUri,
          usePopup: true,
          nonce: prepared.hashedNonce,
          state: prepared.state,
        });
        const res = await appleId.auth.signIn();
        const returnedState = res.authorization.state;
        if (returnedState !== undefined && returnedState !== prepared.state) {
          throw new Error('Apple sign-in state mismatch');
        }
        setError(null);
        await auth.socialSignIn({
          provider: 'apple',
          idToken: res.authorization.id_token,
          code: res.authorization.code,
          nonce: prepared.rawNonce,
          name: joinAppleName(res.user?.name),
        });
      } catch (failure) {
        if (isAppleCancel(failure)) {
          trackEvent('signin_cancelled', { method: 'apple' });
        }
        setError(new Error('Apple sign-in was cancelled'));
      } finally {
        // The nonce is single-use. Get a new one after every attempt.
        appleNonce.refresh();
      }
    };
    attempt().catch(console.error);
  }, [appleNonce, auth]);

  return useMemo(
    () => ({
      isEnabled: googleConfigured || appleConfigured,
      googleEnabled: googleConfigured,
      appleEnabled: appleConfigured,
      appleReady: appleNonce.ready,
      appleUnavailable: !appleNonce.ready && appleNonce.failed,
      isBusy: auth.isSocialSigningIn,
      error: error ?? auth.error,
      signInWithGoogle,
      signInWithApple,
    }),
    [
      googleConfigured,
      appleConfigured,
      appleNonce.ready,
      appleNonce.failed,
      auth.isSocialSigningIn,
      auth.error,
      error,
      signInWithGoogle,
      signInWithApple,
    ]
  );
};
